import { AUTH_URL } from 'src/configs';
import request from 'src/request';

import type { ListMeta } from './adminUsers';

/** Matches CUSTOMER_SUPPORT_STATUS on the API. */
export const SUPPORT_TICKET_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'RESOLVED',
  'CLOSED',
] as const;

export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

/** Populated from the user the ticket belongs to; absent for guest tickets. */
export type SupportTicketUser = {
  id?: string;
  _id?: string;
  email?: string;
  name?: { first?: string; last?: string };
  bxlend_id?: string;
  status?: string;
};

export type SupportTicket = {
  id: string;
  email?: string;
  subject?: string;
  description?: string;
  status?: SupportTicketStatus | string;
  /** Public S3 URLs, up to three per ticket. */
  attachment?: string[];
  user_id?: SupportTicketUser | string | null;
  created_at?: string;
  updated_at?: string;
};

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
};

export const formatSupportStatusLabel = (status?: string) => {
  if (!status) return '—';
  return String(status)
    .toUpperCase()
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
};

/** The populated user, when the API sent one rather than a bare id. */
export const getTicketUser = (ticket?: SupportTicket | null): SupportTicketUser | null => {
  const user = ticket?.user_id;
  if (!user || typeof user === 'string') return null;
  return user;
};

export const getTicketUserId = (ticket?: SupportTicket | null) => {
  const user = ticket?.user_id;
  if (!user) return undefined;
  if (typeof user === 'string') return user;
  return user.id ?? user._id;
};

export const formatTicketUserName = (user?: SupportTicketUser | null) => {
  const name = [user?.name?.first, user?.name?.last].filter(Boolean).join(' ').trim();
  return name || user?.email || '—';
};

export const fetchSupportTickets = async (params: {
  page?: number;
  limit?: number;
  status?: string;
  user_id?: string;
}) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/customer-support${buildQuery(params)}`,
  );
  return {
    tickets: (response.data.customer_support_tickets || []) as SupportTicket[],
    meta: response.data.meta as ListMeta,
  };
};

export const updateSupportTicketStatus = async (
  ticketId: string,
  status: SupportTicketStatus,
) => {
  const response = await request.patch(
    `${AUTH_URL}/v1/admin/customer-support/${ticketId}/status`,
    { status },
  );
  return response.data as { customer_support?: SupportTicket };
};
